<template>
  <BaseModal
    :model-value="isOpen"
    :title="displayMenuItem?.name ?? ''"
    max-width-class="sm:max-w-xl"
    panel-class="2xl:h-auto sm:h-[85vh] sm:w-[90%]"
    full-screen-on-mobile
    :show-close-button="false"
    @close="closeModal"
  >
    <template #content>
      <!-- Mobile close button - always visible in top right corner -->
      <div class="absolute right-4 sm:hidden top-4 z-(--z-index-ds-lifted)">
        <button
          type="button"
          class="bg-surface-canvas cursor-pointer focus:outline-none hover:text-content-level-500 p-2 rounded-full shadow-lg text-content-level-400"
          :disabled="isAddingToCart"
          @click="closeModal"
        >
          <span class="sr-only">
            {{ $t('commerce.menuItemModal.close') }}
          </span>

          <svg
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Desktop close button - hidden on mobile -->
      <div class="absolute hidden right-4 sm:block top-4 z-(--z-index-ds-lifted)">
        <button
          type="button"
          class="bg-surface-canvas cursor-pointer dark:bg-surface-level-800 dark:hover:text-content-level-300 focus:outline-none hover:text-content-level-500 p-1 rounded-md shadow-lg text-content-level-400"
          :disabled="isAddingToCart"
          @click="closeModal"
        >
          <span class="sr-only">
            {{ $t('commerce.menuItemModal.close') }}
          </span>

          <svg
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="flex-1 min-h-0 overflow-hidden">
        <!-- Scrollable content area -->
        <div class="2xl:h-auto 2xl:max-h-[70vh] h-full overflow-y-auto">
          <!-- Menu Item Image Section -->
          <div
            class="bg-surface-level-200 dark:bg-surface-level-700 h-[210px] overflow-hidden relative w-full"
          >
            <nuxt-img
              v-if="menuItemImageUrl && !imageLoadFailed"
              :src="menuItemImageUrl"
              v-bind="heroImgAttrs"
              class="absolute h-full inset-0 object-center object-cover w-full"
              :quality="MENU_ITEM_MODAL_IMAGE.quality"
              :format="MENU_ITEM_MODAL_IMAGE.format"
              :width="MENU_ITEM_MODAL_IMAGE.width"
              :height="MENU_ITEM_MODAL_IMAGE.height"
              @error="handleImageError"
            />

            <div
              v-if="!menuItemImageUrl || imageLoadFailed"
              class="bg-surface-level-200 dark:bg-surface-level-700 flex h-full items-center justify-center w-full"
            >
              <Utensils class="h-24 text-content-level-400 w-24" />
            </div>
          </div>

          <!-- Header Content - Becomes sticky when scrolling past image -->
          <div
            class="bg-surface-canvas border-b border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 sticky top-0 z-(--z-index-ds-bar)"
          >
            <div class="px-8 py-4 text-left w-full">
              <!-- Title Row -->
              <div class="mb-0">
                <h2 class="dark:text-content-inverse font-bold text-2xl text-content-level-900">
                  {{ displayMenuItem?.name }}
                </h2>
              </div>

              <!-- Price and serving info -->
              <div
                class="dark:text-content-level-400 flex-shrink-0 py-0 text-content-level-600 text-sm"
              >
                <span class="font-medium">{{
                  $t('commerce.menuItemModal.amountAmountprimary', {
                    amount: basePrice,
                    amountPrimary: priceTypeDisplayLabel
                  })
                }}</span>

                <span
                  v-if="servesGuestCountLabel"
                  class="dark:text-content-level-400 inline-flex items-center ml-2 text-content-level-500"
                >
                  <span class="dark:text-content-level-500 mr-2 text-content-level-400"
                    >&middot;</span
                  >
                  {{ servesGuestCountLabel }}
                </span>
              </div>
            </div>
          </div>

          <!-- Form content with padding -->
          <div class="pb-24 px-8 py-6 space-y-8">
            <!-- Inner container with padding that won't affect scrollbar -->
            <div>
              <!-- Badges and Dietary Options Section -->
              <div class="flex flex-wrap gap-2 items-center mb-4">
                <!-- Most ordered badge -->
                <span
                  v-if="displayMenuItem?.is_popular"
                  class="bg-badge-menu-item-popular dark:bg-badge-menu-item-popular-dark/20 dark:text-content-badge-menu-item-popular-dark font-medium inline-flex items-center px-3 py-1 rounded-full text-content-badge-menu-item-popular text-xs"
                >
                  {{ $t('commerce.menuItemModal.mostOrdered') }}
                </span>

                <!-- Dietary Options -->
                <div
                  v-if="
                    displayMenuItem?.dietary_options && displayMenuItem.dietary_options.length > 0
                  "
                  class="flex items-center space-x-1.5"
                >
                  <DietaryIcon
                    v-for="diet in displayMenuItem.dietary_options"
                    :key="diet.id"
                    :name="getDietaryIconName(diet.slug)"
                    class="h-4 w-4"
                  />
                </div>
              </div>

              <!-- Menu Item Description -->
              <div
                v-if="displayMenuItem?.description"
                v-safe-html="format(displayMenuItem.description)"
                class="dark:text-content-level-300 leading-relaxed mb-6 menu-item-description text-content-level-700 text-sm"
              />

              <!-- Quantity Selection -->
              <div class="mb-8">
                <FormField
                  name="quantity"
                  :label="quantityLabel"
                  :error="quantityError"
                >
                  <template #control>
                    <div
                      class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 dark:focus-within:border-primary dark:focus-within:ring-primary focus-within:border-primary focus-within:ring-2 focus-within:ring-primary h-11 inline-flex items-stretch mt-2 overflow-hidden rounded-lg"
                    >
                      <button
                        type="button"
                        :aria-label="$t('commerce.menuItemModal.decreaseQuantityLabel')"
                        class="bg-surface-level-50 border-border-level-200 border-r cursor-pointer dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-200 disabled:cursor-not-allowed disabled:opacity-50 flex h-full hover:bg-surface-level-100 items-center justify-center shrink-0 text-content-level-700 w-11"
                        :disabled="selectedQuantity <= minimumSelectableQuantity"
                        @click.stop="decrementQuantity"
                      >
                        <span class="font-medium leading-none select-none text-xl">−</span>
                      </button>

                      <input
                        id="quantity"
                        :value="selectedQuantity"
                        type="text"
                        name="quantity"
                        inputmode="numeric"
                        class="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield] bg-surface-clear border-0 dark:text-content-inverse focus:outline-none focus:ring-0 font-medium h-full max-w-[4rem] min-w-[3rem] px-2 shrink-0 text-base text-center text-content-level-900"
                        @input="handleQuantityInput"
                        @change="commitQuantity"
                      />

                      <button
                        type="button"
                        :aria-label="$t('commerce.menuItemModal.increaseQuantityLabel')"
                        class="bg-surface-level-50 border-border-level-200 border-l cursor-pointer dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-200 disabled:cursor-not-allowed disabled:opacity-50 flex h-full hover:bg-surface-level-100 items-center justify-center shrink-0 text-content-level-700 w-11"
                        @click.stop="incrementQuantity"
                      >
                        <span class="font-medium leading-none select-none text-xl">+</span>
                      </button>
                    </div>
                  </template>
                </FormField>

                <p
                  v-if="isPerPersonPricing && minimumSelectableQuantity > 1"
                  class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm"
                >
                  {{
                    $t('commerce.menuItemModal.minimumQuantityGuestsRequiredForThisItem', {
                      quantity: minimumSelectableQuantity
                    })
                  }}
                </p>
              </div>

              <!-- Customizations -->
              <div
                v-for="customization in displayMenuItem?.customizations"
                :key="customization.id"
                class="mb-8"
              >
                <div class="flex items-center justify-between mb-0">
                  <h3
                    class="dark:text-content-inverse font-semibold text-content-level-900 text-lg"
                  >
                    {{ customization.name }}
                  </h3>

                  <span
                    v-if="customization.is_required"
                    class="bg-info-100 dark:bg-info-900/30 dark:text-info-300 font-medium inline-flex items-center px-2 py-1 rounded-full text-info-800 text-xs"
                  >
                    {{ $t('commerce.menuItemModal.required') }}
                  </span>

                  <span
                    v-else
                    class="bg-surface-level-100 dark:bg-surface-level-700/60 dark:text-content-level-300 font-medium inline-flex items-center px-2 py-1 rounded-full text-content-level-700 text-xs"
                  >
                    {{ $t('commerce.menuItemModal.optional') }}
                  </span>
                </div>

                <!-- Selection requirements -->
                <p
                  v-if="getSelectionRequirementLabel(customization)"
                  class="dark:text-content-level-400 mb-4 text-content-level-500 text-sm"
                >
                  {{ getSelectionRequirementLabel(customization) }}
                </p>

                <p
                  v-if="shouldShowSelectionValidationMessage(customization)"
                  class="dark:text-danger-400 mb-4 text-danger-600 text-sm"
                >
                  {{ getSelectionValidationMessage(customization) }}
                </p>

                <!-- Radio buttons for single selection -->
                <div
                  v-if="
                    !customization.allow_multiple_selections && customization.max_selections === 1
                  "
                  class="space-y-0"
                >
                  <div
                    v-for="(option, index) in customization.options"
                    :key="option.id"
                    class="flex items-center justify-between py-2 relative"
                    :class="{
                      'pb-2': !option.price_adjustment || (option.price_adjustment ?? 0) === 0,
                      'border-b border-border-level-100 dark:border-border-level-700':
                        index < customization.options.length - 1
                    }"
                  >
                    <div class="flex-1">
                      <label
                        :for="`${customization.id}-${option.id}`"
                        class="dark:text-content-inverse font-medium text-content-level-900 text-sm"
                      >
                        {{ option.name }}
                      </label>

                      <p
                        v-if="option.description"
                        class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs"
                      >
                        {{ option.description }}
                      </p>

                      <p
                        v-if="option.price_adjustment && (option.price_adjustment ?? 0) > 0"
                        class="mt-1 text-primary text-xs"
                      >
                        {{
                          $t('commerce.menuItemModal.amount', {
                            amount: option.price_adjustment
                          })
                        }}
                      </p>
                    </div>

                    <div class="flex items-center ml-3">
                      <label
                        :for="`${customization.id}-${option.id}`"
                        class="inline-flex items-center relative"
                        :class="
                          option.is_available === false
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer'
                        "
                      >
                        <input
                          :id="`${customization.id}-${option.id}`"
                          :name="`customization-${customization.id}`"
                          type="radio"
                          class="peer sr-only"
                          :disabled="option.is_available === false"
                          :checked="getOptionQuantity(customization.id, option.id) > 0"
                          @change="() => selectSingleOption(customization.id, option.id)"
                        />

                        <span
                          class="bg-surface-clear border border-primary dark:peer-disabled:border-border-level-600 flex h-5 items-center justify-center peer-disabled:border-border-level-300 peer-disabled:opacity-60 rounded-full transition-colors w-5"
                        >
                          <span
                            v-if="getOptionQuantity(customization.id, option.id) > 0"
                            class="bg-primary h-3 rounded-full w-3"
                          />
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Quantity selector for repeatable options -->
                <div
                  v-else-if="shouldUseQuantitySelector(customization)"
                  class="space-y-0"
                >
                  <div
                    v-for="(option, index) in customization.options"
                    :key="option.id"
                    class="flex gap-4 items-center justify-between py-3 relative"
                    :class="{
                      'border-b border-border-level-100 dark:border-border-level-700':
                        index < customization.options.length - 1
                    }"
                  >
                    <div class="flex-1">
                      <label
                        class="dark:text-content-inverse font-medium text-content-level-900 text-sm"
                      >
                        {{ option.name }}
                      </label>

                      <p
                        v-if="option.description"
                        class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs"
                      >
                        {{ option.description }}
                      </p>

                      <p
                        v-if="option.price_adjustment && (option.price_adjustment ?? 0) > 0"
                        class="mt-1 text-primary text-xs"
                      >
                        {{
                          $t('commerce.menuItemModal.amount', {
                            amount: option.price_adjustment
                          })
                        }}
                      </p>
                    </div>

                    <div
                      class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 h-10 inline-flex items-stretch overflow-hidden rounded-lg"
                    >
                      <button
                        type="button"
                        :aria-label="$t('commerce.menuItemModal.decreaseOptionQuantityLabel')"
                        class="bg-surface-level-50 border-border-level-200 border-r dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-200 disabled:cursor-not-allowed disabled:opacity-50 flex h-full hover:bg-surface-level-100 items-center justify-center text-content-level-700 w-10"
                        :disabled="getOptionQuantity(customization.id, option.id) <= 0"
                        @click="() => decrementOption(customization.id, option.id)"
                      >
                        <span class="font-medium leading-none select-none text-lg">−</span>
                      </button>

                      <div
                        class="dark:text-content-inverse flex font-semibold items-center justify-center min-w-[2.75rem] px-3 text-content-level-900 text-sm"
                      >
                        {{ getOptionQuantity(customization.id, option.id) }}
                      </div>

                      <button
                        type="button"
                        :aria-label="$t('commerce.menuItemModal.increaseOptionQuantityLabel')"
                        class="bg-surface-level-50 border-border-level-200 border-l dark:bg-surface-level-700 dark:border-border-level-600 dark:hover:bg-surface-level-600 dark:text-content-level-200 disabled:cursor-not-allowed disabled:opacity-50 flex h-full hover:bg-surface-level-100 items-center justify-center text-content-level-700 w-10"
                        :disabled="
                          !canIncrementOption(customization, option.id, isOptionAvailable(option))
                        "
                        @click="
                          () => incrementOption(customization, option.id, isOptionAvailable(option))
                        "
                      >
                        <span class="font-medium leading-none select-none text-lg">+</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Checkboxes for multiple selection -->
                <div
                  v-else
                  class="space-y-0"
                >
                  <div
                    v-for="(option, index) in customization.options"
                    :key="option.id"
                    class="flex items-center justify-between py-2 relative"
                    :class="{
                      'pb-2': !option.price_adjustment || (option.price_adjustment ?? 0) === 0,
                      'border-b border-border-level-100 dark:border-border-level-700':
                        index < customization.options.length - 1,
                      'cursor-pointer': canSelectOption(
                        customization,
                        option.id,
                        isOptionAvailable(option)
                      ),
                      'cursor-not-allowed opacity-60': !canSelectOption(
                        customization,
                        option.id,
                        isOptionAvailable(option)
                      )
                    }"
                    @click.prevent="() => toggleAvailableOption(customization, option)"
                  >
                    <div class="flex-1">
                      <span
                        class="dark:text-content-inverse font-medium text-content-level-900 text-sm"
                      >
                        {{ option.name }}
                      </span>

                      <p
                        v-if="option.description"
                        class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs"
                      >
                        {{ option.description }}
                      </p>

                      <p
                        v-if="option.price_adjustment && (option.price_adjustment ?? 0) > 0"
                        class="mt-1 text-primary text-xs"
                      >
                        {{
                          $t('commerce.menuItemModal.amount', {
                            amount: option.price_adjustment
                          })
                        }}
                      </p>
                    </div>

                    <div class="flex items-center ml-3 pointer-events-none">
                      <SharedAppCheckbox
                        :id="`${customization.id}-${option.id}`"
                        size="sm"
                        :model-value="getOptionQuantity(customization.id, option.id) > 0"
                        :disabled="
                          !canSelectOption(customization, option.id, isOptionAvailable(option))
                        "
                        readonly
                      />
                    </div>
                  </div>
                </div>

                <div
                  v-for="selection in getUnavailableSelections(customization)"
                  :key="selection.optionId"
                  class="flex items-center justify-between py-2"
                >
                  <span class="text-sm">{{ selection.name }}</span>

                  <button
                    type="button"
                    class="btn btn-ghost"
                    :aria-label="
                      $t('commerce.menuItemModal.removeOptionLabel', { name: selection.name })
                    "
                    @click="() => setOptionQuantity(customization.id, selection.optionId, 0)"
                  >
                    {{ $t('commerce.menuItemModal.remove') }}
                  </button>
                </div>
              </div>

              <!-- Special Instructions -->
              <div class="mb-8">
                <TextareaField
                  v-model="specialInstructions"
                  name="special_instructions"
                  :label="$t('commerce.menuItemModal.specialInstructions')"
                  :rows="3"
                  :placeholder="$t('commerce.menuItemModal.specialRequestsPlaceholder')"
                  :error="noteError"
                  :maxlength="ORDER_LINE_SPECIAL_INSTRUCTIONS_MAX"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal footer - fixed at bottom, above scrollable content -->
      <div
        class="absolute bg-surface-canvas border-border-level-200 border-t bottom-0 dark:bg-surface-level-800 dark:border-border-level-700 flex items-center justify-between left-0 px-8 py-4 right-0 z-(--z-index-ds-bar)"
      >
        <div class="text-left">
          <div class="dark:text-content-inverse font-semibold text-content-level-900 text-lg">
            {{ $t('commerce.menuItemModal.amountSummary', { amount: totalPrice }) }}
          </div>

          <div class="dark:text-content-level-400 text-content-level-500 text-sm">
            {{
              $t('commerce.menuItemModal.quantityAmountAmountprimary', {
                quantity: selectedQuantity,
                amount: basePrice,
                amountPrimary: priceSummarySuffix
              })
            }}
          </div>

          <div
            v-if="requiresOrderDetails"
            class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm"
          >
            {{ $t('commerce.menuItemModal.selectDateTimeToAddThisItem') }}
          </div>
        </div>

        <span
          class="inline-flex rounded-lg"
          :class="{ 'cursor-not-allowed': isAddToCartDisabled && !requiresOrderDetails }"
          @click.stop.prevent="handleAddToCartAttempt"
        >
          <button
            type="button"
            class="btn gap-2 inline-flex items-center"
            :class="addToCartButtonClasses"
            :disabled="isAddToCartDisabled"
          >
            <span
              v-if="isAddingToCart"
              class="loading loading-spinner loading-xs"
              aria-hidden="true"
            />
            {{ addToCartButtonLabel }}
          </button>
        </span>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { formatSelectionRequirementLabel } from '~/utils/formatSelectionRequirementLabel'
import { indexFirstById } from '~/utils/indexFirstById'
import { Utensils } from '@lucide/vue'
import { computed, ref, shallowRef, watch } from 'vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import DietaryIcon from '~/components/DietaryIcon.vue'
import SharedAppCheckbox from '~/components/shared/AppCheckbox.vue'
import { resolveMenuItemImageUrl } from '~/composables/resolveMenuItemImageUrl'
import { MENU_ITEM_MODAL_IMAGE } from '~/composables/MENU_ITEM_MODAL_IMAGE'
import { useMenuItemPriceTypeCopy } from '~/composables/useMenuItemPriceTypeCopy'
import { useMenuDescriptionFormat } from '~/composables/useMenuDescriptionFormat'
import { vSafeHtml } from '~/utils/vSafeHtml'
import type { Customization } from '~/types/Customization'
import { useCustomizationSelection } from '~/composables/useCustomizationSelection'
import type { SelectedCustomizationQuantities } from '~/types/SelectedCustomizationQuantities'
import { type DietaryOptionType, MenuItemPriceType } from '~/enums'
import FormField from '~/design-system/molecules/FormField/FormField.molecule.vue'
import TextareaField from '~/design-system/molecules/TextareaField.molecule.vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { validateOrderLineItem } from '~/composables/validateOrderLineItem'
import { ORDER_LINE_SPECIAL_INSTRUCTIONS_MAX } from '~/services/api/contracts/bounds/ORDER_LINE_SPECIAL_INSTRUCTIONS_MAX'

// Types
interface DietaryOption {
  id: number
  name: string
  slug: string
  icon?: string
  color?: string
  type: DietaryOptionType
}

interface MenuItem {
  id: number
  name: string
  description: string | null
  price: string
  price_type?: MenuItemPriceType
  image?: string
  serves_count: number | string | null
  minimum_guest_count?: number
  is_package?: boolean
  is_popular?: boolean
  individual_packaging_available?: boolean
  dietary_options?: DietaryOption[]
  customizations?: Customization[]
}

type DietaryIconName =
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'vegetarian'
  | 'vegan'
  | 'low-carb'
  | 'keto'
  | 'halal'
  | 'kosher'

interface CartItemPayload {
  menuItem: MenuItem
  quantity: number
  customizations: SelectedCustomizationQuantities
  special_instructions: string
  totalPrice: string
  totalPricePerPerson: string
}

// Props
const props = withDefaults(
  defineProps<{
    /** Controls whether the modal is visible. */
    isOpen: boolean
    /** Menu item displayed by the modal. */
    menuItem: MenuItem | null
    /** Enables item editing controls. */
    editMode?: boolean
    /** Indicates that the item is being added to the cart. */
    isAddingToCart?: boolean
    /** Indicates whether order details are required. */
    requiresOrderDetails?: boolean
    /** Quantity initially selected for the item. */
    initialQuantity?: number | undefined
    /** Initial item customization selections. */
    initialCustomizations?: SelectedCustomizationQuantitiesInput | undefined
    /** Initial special instructions for the item. */
    initialSpecialInstructions?: string | undefined
  }>(),
  {
    editMode: false,
    initialCustomizations: undefined,
    initialQuantity: undefined,
    initialSpecialInstructions: undefined,
    isAddingToCart: false,
    requiresOrderDetails: false
  }
)

// Emits
const emit = defineEmits<{
  close: []
  addToCart: [item: CartItemPayload]
  selectDateTime: []
}>()

const { format } = useMenuDescriptionFormat()
const priceTypeCopy = useMenuItemPriceTypeCopy()
const t = usePresentationTranslator()

// Keeps the last item rendered while the leave transition plays.
const lastKnownMenuItem = shallowRef<MenuItem | null>(null)
watch(
  () => props.menuItem,
  (item) => {
    if (item) lastKnownMenuItem.value = item
  },
  { immediate: true }
)
const displayMenuItem = computed(() => props.menuItem ?? lastKnownMenuItem.value)

const menuItemImageUrl = computed(() => resolveMenuItemImageUrl(displayMenuItem.value))
// NuxtImg's declared prop type doesn't include native img attrs like `loading`
// or `fetchpriority`; they still forward correctly via $attrs at runtime.
const heroImgAttrs = computed(
  () =>
    ({ alt: displayMenuItem.value?.name ?? '', fetchpriority: 'high', loading: 'eager' }) as const
)

type SelectedCustomizationQuantitiesInput = Record<number, Record<number, number>>

// State
const selectedQuantity = ref(1)
const {
  allowsRepeatedSelection,
  decrementOption,
  getCustomizationSelectionCount,
  getCustomizationUniqueSelectionCount,
  getMaxTotalSelections,
  getOptionQuantity,
  incrementOption,
  canSelectOption,
  canIncrementOption,
  isOptionQuantityValid,
  quantities: selectedCustomizations,
  selectSingleOption,
  setOptionQuantity,
  toggleOption
} = useCustomizationSelection()
const specialInstructions = ref('')
const hasAttemptedAddToCart = ref(false)
const imageLoadFailed = ref(false)
const handleImageError = () => {
  imageLoadFailed.value = true
}
const decrementQuantity = () => {
  selectedQuantity.value = Math.max(minimumSelectableQuantity.value, selectedQuantity.value - 1)
}
const incrementQuantity = () => {
  selectedQuantity.value = selectedQuantity.value + 1
}
const parseQuantityDigits = (raw: string): number | null => {
  const digits = raw.replace(/\D/g, '')
  const quantity = Number(digits)
  return digits !== '' && Number.isSafeInteger(quantity) ? quantity : null
}
const handleQuantityInput = (event: Event): void => {
  const input = event.target
  if (input instanceof HTMLInputElement) {
    const quantity = parseQuantityDigits(input.value)
    input.value = input.value.replace(/\D/g, '')
    if (quantity !== null) selectedQuantity.value = quantity
  }
}
const commitQuantity = (event: Event): void => {
  const input = event.target
  if (input instanceof HTMLInputElement) {
    selectedQuantity.value = parseQuantityDigits(input.value) ?? minimumSelectableQuantity.value
    input.value = String(selectedQuantity.value)
  }
}

// Reset image error state when the menu item changes
watch(
  () => props.menuItem?.id,
  () => {
    imageLoadFailed.value = false
  }
)

// Computed
const basePrice = computed(() => {
  return displayMenuItem.value ? parseFloat(displayMenuItem.value.price).toFixed(2) : '0.00'
})

const isPerPersonPricing = computed(
  () => displayMenuItem.value?.price_type === MenuItemPriceType.PER_PERSON
)
const minimumSelectableQuantity = computed(() => {
  const configuredMinimum = Number(displayMenuItem.value?.minimum_guest_count)

  if (isPerPersonPricing.value && Number.isFinite(configuredMinimum) && configuredMinimum > 0) {
    return configuredMinimum
  }

  return 1
})

const priceTypeDisplayLabel = computed(() =>
  priceTypeCopy.unitLabel(displayMenuItem.value?.price_type ?? MenuItemPriceType.EACH)
)

const quantityLabel = computed(() => {
  return isPerPersonPricing.value ? 'Select guests' : 'Select quantity'
})

const priceSummarySuffix = computed(() => {
  return isPerPersonPricing.value ? ' per person' : ''
})

const lineItemValidation = computed(() =>
  validateOrderLineItem(
    {
      menuItemId: displayMenuItem.value?.id ?? 0,
      quantity: selectedQuantity.value,
      specialInstructions: specialInstructions.value
    },
    t
  )
)

const quantityError = computed(() => {
  if (selectedQuantity.value < minimumSelectableQuantity.value) {
    return t('commerce.menuItemModal.minimumQuantityGuestsRequiredForThisItem', {
      quantity: minimumSelectableQuantity.value
    })
  }
  return lineItemValidation.value.quantity ?? ''
})

const noteError = computed(() => lineItemValidation.value.special_instructions ?? '')

const isAddToCartDisabled = computed(() => {
  if (props.requiresOrderDetails) return false
  if (quantityError.value !== '' || noteError.value !== '') return true
  return !isValidSelection.value || props.isAddingToCart
})

const addToCartButtonLabel = computed(() => {
  if (props.requiresOrderDetails) return 'Select Date & Time'
  if (props.isAddingToCart) return 'Adding...'
  return props.editMode ? 'Update Item' : 'Add to Cart'
})

const addToCartButtonClasses = computed(() => {
  if (isAddToCartDisabled.value) {
    return [
      '!border-border-level-700 !bg-surface-level-700 !text-content-level-100 !opacity-100',
      'disabled:!border-border-level-700 disabled:!bg-surface-level-700 disabled:!text-content-level-100',
      'dark:!border-border-level-600 dark:!bg-surface-level-600 dark:!text-content-level-100',
      'dark:disabled:!border-border-level-600 dark:disabled:!bg-surface-level-600 dark:disabled:!text-content-level-100',
      'pointer-events-none'
    ]
  }

  return 'btn-active btn-primary text-content-inverse'
})

const getSelectionRequirementLabel = (customization: Customization) =>
  formatSelectionRequirementLabel({
    allowsRepeatedSelection: allowsRepeatedSelection(customization),
    maxSelections: Number(customization.max_selections) || 0,
    maxTotalSelections: getMaxTotalSelections(customization),
    minSelections: Number(customization.min_selections) || 0
  })

const formattedServesCount = computed(() => {
  const rawValue = String(displayMenuItem.value?.serves_count ?? '').trim()
  if (!rawValue) return ''

  return rawValue.replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ')
})

const servesGuestCountLabel = computed(() => {
  if (!formattedServesCount.value) return ''

  return formattedServesCount.value.includes(' - ')
    ? `Serves guest count: ${formattedServesCount.value}`
    : `Serves ${formattedServesCount.value} guests`
})

const totalServings = computed(() => {
  const servesCount = Number(displayMenuItem.value?.serves_count)
  return Number.isFinite(servesCount) ? servesCount * selectedQuantity.value : 0
})

const customizationPriceAdjustment = computed(() => {
  let total = 0
  Object.entries(selectedCustomizations.value).forEach(([customizationId, optionQuantities]) => {
    const customization = displayMenuItem.value?.customizations?.find(
      (item) => item.id === Number(customizationId)
    )

    if (!customization) return

    Object.entries(optionQuantities).forEach(([optionId, quantity]) => {
      if (quantity <= 0) return

      const option = customization.options.find((opt) => opt.id === Number(optionId))
      if (!option) return

      total += (option.price_adjustment || 0) * quantity
    })
  })
  return total
})

const totalPrice = computed(() => {
  if (!displayMenuItem.value) return '0.00'
  const base = parseFloat(displayMenuItem.value.price)
  const adjustment = customizationPriceAdjustment.value
  const total = isPerPersonPricing.value
    ? base * selectedQuantity.value + adjustment
    : (base + adjustment) * selectedQuantity.value
  return total.toFixed(2)
})

const totalPricePerPerson = computed(() => {
  if (totalServings.value === 0) return '0.00'
  const total = parseFloat(totalPrice.value)
  return (total / totalServings.value).toFixed(2)
})

const isValidSelection = computed(() =>
  (props.menuItem?.customizations ?? []).every(
    (customization) => getSelectionValidationMessage(customization) === ''
  )
)

const isOptionAvailable = (option: Customization['options'][number]) => option.is_available

const toggleAvailableOption = (
  customization: Customization,
  option: Customization['options'][number]
) => {
  toggleOption(customization, option.id, isOptionAvailable(option))
}

const getUnavailableSelections = (customization: Customization) =>
  Object.entries(selectedCustomizations.value[customization.id] ?? {}).flatMap(
    ([selectedId, quantity]) => {
      const hasSelectedQuantity = quantity > 0
      if (!hasSelectedQuantity) return []

      const optionId = Number(selectedId)
      const option = customization.options.find((candidate) => candidate.id === optionId)
      const hasAvailableOption = option !== undefined && isOptionAvailable(option)
      if (hasAvailableOption) return []

      return [
        {
          name: option?.name ?? t('commerce.menuItemModal.unavailableOption', { optionId }),
          optionId
        }
      ]
    }
  )

// Methods
const closeModal = () => {
  if (props.isAddingToCart) return

  emit('close')
}

const dietaryIconNames: DietaryIconName[] = [
  'gluten-free',
  'dairy-free',
  'nut-free',
  'vegetarian',
  'vegan',
  'low-carb',
  'keto',
  'halal',
  'kosher'
]

const isDietaryIconName = (value: string): value is DietaryIconName => {
  return dietaryIconNames.some((iconName) => iconName === value)
}

const getDietaryIconName = (slug: string): DietaryIconName => {
  // Map API slugs to DietaryIcon component names
  return isDietaryIconName(slug) ? slug : 'vegetarian'
}

const addToCart = () => {
  if (!props.menuItem || props.isAddingToCart) return

  if (!isValidSelection.value) {
    hasAttemptedAddToCart.value = true
    return
  }

  const cartItem: CartItemPayload = {
    customizations: selectedCustomizations.value,
    menuItem: props.menuItem,
    quantity: selectedQuantity.value,
    special_instructions: specialInstructions.value,
    totalPrice: totalPrice.value,
    totalPricePerPerson: totalPricePerPerson.value
  }

  emit('addToCart', cartItem)
}

const handleAddToCartAttempt = () => {
  if (props.isAddingToCart) return
  if (props.requiresOrderDetails) {
    emit('selectDateTime')
    return
  }

  addToCart()
}

const shouldUseQuantitySelector = (customization: Customization) => {
  return customization.allow_multiple_selections && allowsRepeatedSelection(customization)
}

// Repeated-selection groups cap the total count; other groups have no total cap (`Infinity`).
const getRepeatedSelectionCap = (customization: Customization): number =>
  allowsRepeatedSelection(customization) ? getMaxTotalSelections(customization) : Infinity

const getSelectionValidationMessage = (customization: Customization) => {
  const optionsById = indexFirstById(customization.options)
  const selections = Object.entries(selectedCustomizations.value[customization.id] ?? {})
  for (const [optionId, quantity] of selections) {
    const hasSelectedQuantity = quantity > 0
    if (!hasSelectedQuantity) continue

    const option = optionsById.get(Number(optionId))
    const hasAvailableOption = option !== undefined && isOptionAvailable(option)
    if (!hasAvailableOption) return t('commerce.menuItemModal.removeUnavailableOptions')

    if (!isOptionQuantityValid(customization, Number(optionId))) {
      return t('commerce.menuItemModal.reduceOptionQuantity', {
        count: customization.max_per_option
      })
    }
  }

  const totalCount = getCustomizationSelectionCount(customization.id)
  const minSelections = Number(customization.min_selections) || 0
  const hasMinimumSelections = totalCount >= minSelections
  if (customization.is_required && !hasMinimumSelections) {
    return t('commerce.menuItemModal.selectAtLeast', { count: minSelections })
  }

  const selectionCap = getRepeatedSelectionCap(customization)
  const hasValidTotal = totalCount <= selectionCap
  if (!hasValidTotal) return t('commerce.menuItemModal.selectNoMoreThan', { count: selectionCap })

  const uniqueCount = getCustomizationUniqueSelectionCount(customization.id)
  const hasValidDistinctCount =
    customization.max_selections <= 0 || uniqueCount <= customization.max_selections
  if (!hasValidDistinctCount) {
    return t('commerce.menuItemModal.selectNoMoreThanDifferentOptions', {
      count: customization.max_selections
    })
  }

  return ''
}

const shouldShowSelectionValidationMessage = (customization: Customization) => {
  return hasAttemptedAddToCart.value && Boolean(getSelectionValidationMessage(customization))
}

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) initializeSelections()
  }
)

const initializeSelections = () => {
  hasAttemptedAddToCart.value = false
  const nextQuantity = props.initialQuantity ?? minimumSelectableQuantity.value
  selectedQuantity.value = Math.max(minimumSelectableQuantity.value, nextQuantity)
  specialInstructions.value = props.initialSpecialInstructions ?? ''

  Object.keys(selectedCustomizations.value).forEach((key) => {
    selectedCustomizations.value[parseInt(key)] = {}
  })

  if (props.menuItem?.customizations) {
    props.menuItem.customizations.forEach((customization) => {
      if (props.editMode && props.initialCustomizations?.[customization.id]) {
        selectedCustomizations.value[customization.id] = {
          ...props.initialCustomizations[customization.id]
        }
      } else {
        selectedCustomizations.value[customization.id] = {}
      }
    })
  }
}

watch(
  () => minimumSelectableQuantity.value,
  (nextMinimum) => {
    if (!props.isOpen) return
    if (selectedQuantity.value < nextMinimum) {
      selectedQuantity.value = nextMinimum
    }
  }
)
</script>

<style scoped>
:deep(.menu-item-description p) {
  margin: 0;
  min-height: 1em;
}

:deep(.menu-item-description p + p) {
  margin-top: 0.5rem;
}

:deep(.menu-item-description ul) {
  list-style: disc;
  margin: 0.5rem 0 0.5rem 1.25rem;
  padding: 0;
}

:deep(.menu-item-description li) {
  margin: 0.125rem 0;
}

:deep(.menu-item-description strong) {
  font-weight: 700;
}
</style>
